/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import _ from 'lodash';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Webcam from 'react-webcam';
import { v4 as uuidv4 } from 'uuid';

import { yupResolver } from '@hookform/resolvers/yup';

import InputField from '@components/input-field';
import Select from '@components/select-box-component';
import Button from '@components/button';
import Icon from '@components/icon-component';
import DeleteModal from '@components/modal/variants/delete-modal';
import Portal from '@components/portal';
import LoadingRound from '@components/loading-round/loading-round';

import { convertBase64ToFile, resizeImage } from '@utilities/attachments';
import useWindowDimensions from '@hooks/useWindowDimensions';
import isMobile from '@hooks/isMobile';
import { useToastContext } from '@context/toaster.context';

import './add-photo.scss';
import style from './style.module.scss';
import {
  deleteAssetInIndexDB,
  getAssetDataBase64InIndexDB,
  setAssetDataInIndexDB,
  updateAssetFile
} from '@utilities/photos-documents.util';
import { heicTo } from 'heic-to';
import SaveModal from '@components/modal/variants/save-modal';
import { useBlocker } from 'react-router-dom';
import PhotoUI from './photo-ui';

const AddPhoto = ({
  open,
  closed,
  title,
  onSubmitPhoto,
  updatePhoto = {},
  id,
  initialValue,
  deleteBtnEvent
}) => {
  const { ToasterUtil } = useToastContext();
  const { width } = useWindowDimensions();
  const [deletePic, setDeletePic] = useState(false);
  const [selectedFile, setSelectedFile] = useState([]); //1, 2, 3, 4, 5, 6, 7, 8, 9, 10
  const [loadingDelete, setLoadingDelete] = useState(false);
  const fileInputRef = useRef(null);
  const [saveChanges, setSaveChanges] = useState(false);

  const webcamRef = useRef(null);

  const [editPhoto, setEditPhoto] = useState(_.isEmpty(updatePhoto) ? false : true);
  const [openWebCamera, setOpenWebCamera] = useState(false);
  const [mediaStream, setMediaStream] = useState({});
  const [loading, setLoading] = useState(false);
  const { photoCategoryData } = useSelector((state) => state.picklists);

  useEffect(() => {
    return () => {
      setDeletePic(false);
      setSelectedFile([]);
      setLoadingDelete(false);
      setEditPhoto(false);
      setOpenWebCamera(false);
      setMediaStream({});
      setLoading(false);
    };
  }, []);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty, dirtyFields },
    getValues
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      category: initialValue?.category || '',
      caption: initialValue?.caption || '',
      image: initialValue?.image ? [initialValue?.image] : []
    }
  });

  let blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      ((isDirty && Object.keys(dirtyFields)?.length) || open) &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (isDirty && Object.keys(dirtyFields)?.length) setSaveChanges(true);
      else resetAllData();
    }
  }, [blocker, dirtyFields]);

  useEffect(() => {
    const event = async () => {
      if (!_.isEmpty(initialValue)) {
        const fileFromIndexDB = await getAssetDataBase64InIndexDB({
          assetId: initialValue?.image?.id,
          file: true
        });
        setValue('image', initialValue?.image ? [initialValue?.image] : null);
        setValue('category', initialValue?.category || null);
        setValue('caption', initialValue?.caption || null);
        setSelectedFile(fileFromIndexDB ? [fileFromIndexDB] : null);
      }
    };
    event();
  }, [initialValue]);

  const deleteImage = ({ index }) => {
    // Update React state
    setSelectedFile((prev) => {
      const updatedFiles = prev.filter((_, i) => i !== index);
      return updatedFiles;
    });

    // Update React Hook Form value
    const currentImages = getValues('image') || [];
    const updatedImages = currentImages.filter((_, i) => i !== index);

    setValue('image', updatedImages, {
      shouldDirty: true,
      shouldValidate: true
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Clear the input field
    }
  };

  const savePhotoOnIndexDB = async (data, updateId) => {
    const newId = uuidv4();
    const uniqueId = updateId ? updateId : newId;
    if (!uniqueId) {
      await setAssetDataInIndexDB({
        assetId: uniqueId,
        assetFile: data.image,
        status: 'temporary'
      });
    } else if (!data?.image?.id) {
      await updateAssetFile({ assetId: uniqueId, assetFile: data.image });
    }

    let test = {
      ...data, // this data have content category and type
      image: { id: uniqueId, name: data.image?.name, type: data.image?.type }
    };
    onSubmitPhoto(test, newId, updateId);
  };

  const onSubmitHandler = (data) => {
    let images = data?.image;

    for (let image of images) {
      savePhotoOnIndexDB(
        {
          image,
          caption: data?.caption || '',
          category: data?.category || ''
        },
        id
      );
    }
  };

  const onTest = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleSubmit(onSubmitHandler)(event);
  };

  const handleSave = () => {
    handleSubmit((data) => onSubmitHandler(data))();
    setSaveChanges((prev) => !prev);
  };

  const closedModal = () => {
    // Stop media stream before closing
    if (mediaStream?.active) {
      mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
    }
    reset();
    setValue('caption', '');
    setValue('category', '');
    setValue('image', []);
    closed(false);
  };

  const onCameraChange = (e) => {
    try {
      captureMobilePhoto(e);
      e.target.value = '';
    } catch (error) {
      console.log('Error', e);
    }
  };

  const captureMobilePhoto = async (event) => {
    event.preventDefault();
    setLoading(true);

    const allFilesUploaded = Array.from(event.target.files) || [];
    if (!allFilesUploaded?.length) return;

    for (let fileUpload of allFilesUploaded) {
      let fileUploaded = fileUpload;
      const fileExtension = fileUploaded?.name?.split('.')?.pop()?.toLowerCase();

      const isHeic =
        fileUploaded?.type === 'image/heic' ||
        fileUploaded?.type === 'image/heif' ||
        fileExtension === 'heic' ||
        fileExtension === 'heif';

      if (isHeic) {
        const jpeg = await heicTo({
          blob: fileUploaded,
          type: 'image/jpeg',
          quality: 0.2
        });

        fileUploaded = new File([jpeg], fileUploaded.name.replace(/\.[^.]+$/, '.jpg'), {
          type: 'image/jpeg'
        });
      }
      let sizeInMbs = parseFloat(fileUploaded?.size / (1024 * 1024));

      try {
        if (sizeInMbs > 1) {
          const resizedBlob = await resizeImage(fileUploaded, 1000, 1000);
          const resizedFile = new File([resizedBlob], fileUploaded.name, {
            type: fileUploaded.type
          });

          saveImage({ fileUploaded: resizedFile });
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else {
          saveImage({ fileUploaded });
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      } catch (err) {
        ToasterUtil.showError(`Error for uploading an image`);
      }
    }

    setLoading(false);
  };

  const saveImage = ({ fileUploaded }) => {
    const reader = new FileReader();
    reader.readAsDataURL(fileUploaded);
    reader.onload = () => {
      const base64 = reader.result;
      const filename = 'captureImage.png';
      const file = convertBase64ToFile(base64, filename);

      setSelectedFile((prev) => {
        const updatedFiles = [...prev, file];
        return updatedFiles;
      });

      const currentImages = getValues('image') || [];
      const updatedImages = [...currentImages, file];

      setValue('image', updatedImages, {
        shouldDirty: true,
        shouldValidate: true
      });

      // TEMP DEBUG: trigger render-time crash on next render to test ErrorBoundary
      // setCrashOnNextRender(true);
    };
    reader.onerror = (e) => {
      ToasterUtil.showError(`Error for uploading an image`);
    };
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    const filename = 'captureImage.png';
    const file = convertBase64ToFile(imageSrc, filename);

    setSelectedFile((prev) => {
      const updatedFiles = [...prev, file];
      return updatedFiles;
    });

    const currentImages = getValues('image') || [];
    const updatedImages = [...currentImages, file];

    setValue('image', updatedImages, {
      shouldDirty: true,
      shouldValidate: true
    });

    // setSelectedFile([file]);
    // setValue('image', [file], { shouldDirty: true });
    closeCameraEvent();

    // downloadImage(imageSrc, 'my_custom_filename.jpg');
  }, [webcamRef]);

  const goBack = () => {
    if (isDirty && Object.keys(dirtyFields)?.length) setSaveChanges(true);
    else {
      resetAllData();
    }
  };

  const WebcamComponent = () => (
    <div
      style={{
        marginTop: '12px',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <div
        style={{
          width: width >= 700 ? '700px' : `${width - 32}px`,
          maxHeight: 520,
          position: 'relative'
        }}
      >
        <Webcam
          audio={false}
          style={{
            maxHeight: 520
          }}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          width={width}
          videoConstraints={{
            width: width,
            maxHeight: 520,
            // facingMode: 'user'
            // facingMode: { exact: 'environment' } // S
            facingMode: 'environment'
          }}
          wrap="div"
        />

        <Icon
          name={'delete-image-icon'}
          alt=""
          className={'positionIcon'}
          onClick={closeCameraEvent}
          style={{ position: 'absolute', cursor: 'pointer', top: 0, right: 0, zIndex: 9 }}
        />
      </div>
      <div
        style={{
          marginTop: '32px'
        }}
      >
        <Button onClick={capture} text={'Capture photo'} className="addPhotoBtn" />
      </div>
    </div>
  );
  const openCameraEvent = async () => {
    const stream = await navigator?.mediaDevices?.getUserMedia({ video: true });
    if (stream?.active) {
      setOpenWebCamera(true);
      setMediaStream(stream);
    }
  };
  const closeCameraEvent = async () => {
    // Stop media stream when closing camera
    if (mediaStream?.active) {
      mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      setMediaStream({});
    }
    setOpenWebCamera(false);
  };

  useEffect(() => {
    return () => {
      if (mediaStream?.active) {
        mediaStream.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, [mediaStream, openWebCamera]);

  const handleDeleteEvent = async () => {
    setLoadingDelete(true);
    try {
      await deleteAssetInIndexDB({ assetId: initialValue?.id, deleteAll: true });
    } catch (error) {
      console.log('error deleting data');
    }

    deleteBtnEvent(initialValue?.id, initialValue?.image?.id);
    setDeletePic(false);
    setLoadingDelete(false);
    closedModal();
  };

  const resetAllData = () => {
    closed(false);
    blocker?.reset?.();
    reset();
  };

  const isSaveButtonDisabled = useMemo(() => {
    return !isDirty && !Object.keys(dirtyFields)?.length ? true : false;
  }, [isDirty, dirtyFields]);

  const showAddPhotoBtn = id && selectedFile?.length ? false : true;

  return (
    <Portal>
      <form onSubmit={onTest} id="addPhoto">
        <div className={open ? style.equipmentMain : style.equipmentNone}>
          <>
            <div className={`${style.header} px-4 md:px-8 py-7`}>
              <p className={style.cancelClass} onClick={() => goBack()}>
                Cancel
              </p>
              <h1 className={` ${style.addClass} h1-semibold`}>{title}</h1>
              <div className={style.invisibleDiv}>Cancel</div>
            </div>

            <div className={style.photoContentBody}>
              <input
                multiple={id ? false : true}
                type="file"
                id="add-photo"
                ref={fileInputRef}
                // accept="image/*;capture=camera"
                accept="image/*,.jpg;capture=camera,text/plain"
                onChange={onCameraChange}
                style={{ display: 'none' }}
              />

              {selectedFile?.length ? (
                <div>
                  <div className={selectedFile?.length !== 1 && style.photoGrid2}>
                    {selectedFile.map((files, index) => (
                      <PhotoUI
                        selectedFile={files}
                        deleteImage={() => deleteImage({ index })}
                        key={index}
                        singleImage={selectedFile?.length === 1}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <></>
              )}

              {openWebCamera ? (
                WebcamComponent()
              ) : loading ? (
                <LoadingRound className={style.loaderHeight} />
              ) : (
                <>
                  {showAddPhotoBtn && (
                    <div className={style.photoBtnGrid}>
                      {!(isMobile.any() || (isMobile.ipadPro && width <= 1024)) && (
                        <Button
                          iconStart={<Icon name={'camera'} height={16} width={16} />}
                          onClick={openCameraEvent}
                          variant={'pill'}
                          text={'Open camera'}
                          className={style.addPhotoBtn}
                        />
                      )}
                      <label
                        htmlFor="add-photo"
                        className={style.addDocumentBtn}
                        style={{ cursor: 'pointer' }}
                      >
                        <Icon name={'image'} height={16} width={16} />{' '}
                        {selectedFile?.length ? 'ATTACH A PHOTO' : 'Choose existing'}
                      </label>
                    </div>
                  )}
                </>
              )}

              <div className={style.photoGrid}>
                <div className="op-input-field-wrap ">
                  <Select
                    label="Category"
                    options={photoCategoryData?.map((x) => {
                      return {
                        label: x?.value,
                        value: x?.key
                      };
                    })}
                    register={register}
                    name="category"
                    control={control}
                    isClearable
                  />
                </div>
                <div className="op-input-field-wrap ">
                  <InputField
                    label="Caption"
                    type="text"
                    inputClass="input-field input-regular"
                    register={register}
                    name="caption"
                    onInputFocus={(e) => {
                      const element = e.target;
                      element.classList.add('keyboard');
                      window.scrollTo(0, 0);
                    }}
                    onInputBlur={(e) => {
                      const element = e.target;
                      element.classList.remove('keyboard');
                    }}
                    limit={250}
                  />
                  <p className="label-bold text-right text-grey-400 mt-1">
                    {watch('caption')?.length || 0}/250
                  </p>
                </div>
              </div>
              <div className="mb-[320px]"></div>
            </div>
          </>
          <div className="footerBtn">
            {editPhoto && (
              <Icon
                name={'Delete button'}
                alt=""
                style={{ marginRight: editPhoto ? '48px' : '0px' }}
                onClick={() => {
                  setDeletePic(true);
                }}
              />
            )}

            <Button
              btnClass="btn-fill button-large"
              type="submit"
              form="addPhoto"
              text="SAVE"
              disabled={watch('image')?.length === 0 ? true : false}
            />
          </div>
        </div>
      </form>

      <DeleteModal
        open={deletePic}
        handleDelete={handleDeleteEvent}
        setOpen={loadingDelete ? () => {} : setDeletePic}
        confirmDeletion
        recordType="photo"
        disabledDeleteButton={loadingDelete}
      />

      {saveChanges && (
        <SaveModal
          open={saveChanges}
          setOpen={setSaveChanges}
          saveChanges={true}
          onDiscard={() => {
            resetAllData();
          }}
          onSave={handleSave}
        />
      )}
    </Portal>
  );
};
export default AddPhoto;

const schema = yup.object().shape({
  category: yup.string().nullable(),
  caption: yup.string().nullable(),
  image: yup.array().of(yup.mixed()).nullable()
});
